import { useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { SnackBar } from "src/design/SnackBar";
import { InfoCard } from "src/design/InfoCard";
import {
  AccountRequest,
  useAccountRequests,
  useApproveAccountRequest,
  useRejectAccountRequest,
} from "src/data/admin/queries/accountRequest";
import { AccountRequestsHeader } from "./components/AccountRequestsHeader";
import { AccountRequestsTable } from "./components/AccountRequestsTable";
import { ApproveRequestDialog } from "./components/ApproveRequestDialog";
import { RejectRequestDialog } from "./components/RejectRequestDialog";

interface SnackState {
  open: boolean;
  message: string;
  severity: "success" | "error";
}

const AccountRequests = () => {
  const [pendingApprove, setPendingApprove] = useState<AccountRequest | null>(
    null,
  );
  const [pendingReject, setPendingReject] = useState<AccountRequest | null>(
    null,
  );
  const [snack, setSnack] = useState<SnackState>({
    open: false,
    message: "",
    severity: "success",
  });

  const { data: requests, isLoading } = useAccountRequests();

  const approveMutation = useApproveAccountRequest(() => {
    setSnack({
      open: true,
      message: `Request approved successfully.`,
      severity: "success",
    });
    setPendingApprove(null);
  });

  const rejectMutation = useRejectAccountRequest(() => {
    setSnack({
      open: true,
      message: `Request rejected.`,
      severity: "success",
    });
    setPendingReject(null);
  });

  const showError = (err: unknown) =>
    setSnack({
      open: true,
      message: err instanceof Error ? err.message : "An error occurred",
      severity: "error",
    });

  const handleApprove = (restaurantId: string) => {
    if (!pendingApprove || !restaurantId) return;
    approveMutation.mutate(
      { requestId: pendingApprove.id, restaurantId },
      { onError: showError },
    );
  };

  const handleReject = (reason: string) => {
    if (!pendingReject || !reason.trim()) return;
    rejectMutation.mutate(
      { id: pendingReject.id, reason },
      { onError: showError },
    );
  };

  const allRequests = requests ?? [];
  const pendingCount = allRequests.filter((r) => r.status === "PENDING").length;

  return (
    <>
      <Box>
        <AccountRequestsHeader pendingCount={isLoading ? 0 : pendingCount} />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <InfoCard
            title={`Requests (${allRequests.length})`}
            icon={<AssignmentOutlinedIcon fontSize="small" />}
          >
            <AccountRequestsTable
              requests={allRequests}
              onApprove={setPendingApprove}
              onReject={setPendingReject}
            />
          </InfoCard>
        )}
      </Box>

      <ApproveRequestDialog
        request={pendingApprove}
        isSubmitting={approveMutation.isPending}
        onClose={() => setPendingApprove(null)}
        onConfirm={handleApprove}
      />

      <RejectRequestDialog
        request={pendingReject}
        isSubmitting={rejectMutation.isPending}
        onClose={() => setPendingReject(null)}
        onConfirm={handleReject}
      />

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};

export default AccountRequests;
